import { Grid, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { Button, ButtonColorProps, ButtonVariantProps } from '../../atoms/Button';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { LocationSearchItem } from '../LocationSearchItem';
import { ServiceAllocationSelectorProps } from './ServiceAllocationSelector.props';
import { 
  ArrowUpwardIcon, 
  IconContainer, 
  SecondaryAllocationIcon,
  LiveSearchWrapper,
  StyledBox,
  AllocationSubmitButtonWrap,
} from './ServiceAllocationSelector.styles';
import { ActionPermissions, LocationNodeData } from '../../../@types';
import { ServiceNodeAllocation } from '../../../@types/allocation.type';
import { isUserHasPermission } from '../../../configs/permissions';
import { useSearchAllocationNodes } from '../../../queries/live-search-query';
import { useGetAllocationNodeParentData } from '../../../queries/structure-query';
import { useGetCurrentUser } from '../../../queries/user-query';
import { getAllocationNodeAccess } from '../../../utils/services';
import { Link } from '../../atoms/Link';

export const ServiceAllocationSelector: React.FC<ServiceAllocationSelectorProps> = ({
  allocation,
  defaultAllocationSuggetions,
  multiline= true,
  onSubmit,
  onClose,
  permissions
}) => {
  const [userDepotAllocation, setUserDepotAllocation] = useState<ServiceNodeAllocation>();
  const searchAllocationNodes = useSearchAllocationNodes();
  const getCurrentUserQuery = useGetCurrentUser();
  const getAllocationNodeParentData = useGetAllocationNodeParentData(userDepotAllocation?.node?.id);

  useEffect(() => {
    allocation && setUserDepotAllocation(allocation);
  }, [allocation]);

  useEffect(() => {
    userDepotAllocation?.node?.id && userDepotAllocation?.node?.liveSearch && getAllocationNodeParentData.refetch();
  }, [userDepotAllocation?.node]);

  useEffect(() => {
    getAllocationNodeParentData.data && setUserDepotAllocation({
      ...userDepotAllocation,
      parentNode: {
        id: getAllocationNodeParentData.data.id || 0,
        name: getAllocationNodeParentData.data.name || '',
        code: getAllocationNodeParentData.data.code || '',
        nodeTypeCode: getAllocationNodeParentData.data.nodeTypeCode || '',
        liveSearch: true
      }
    });
  }, [getAllocationNodeParentData.data]);

  const handleSubmit = () => {
    userDepotAllocation?.node?.id && userDepotAllocation?.allocationType && onSubmit({
      nodeId: userDepotAllocation.node.id,
      allocationTypeCode: userDepotAllocation.allocationType
    });
  };

  const handleParentNode = () => {
    userDepotAllocation?.parentNode?.id && userDepotAllocation?.allocationType && onSubmit({
      nodeId: userDepotAllocation.parentNode.id,
      allocationTypeCode: userDepotAllocation.allocationType
    });
  };

  const handleTake = () => {
    getCurrentUserQuery?.data?.siblingNodeId && userDepotAllocation?.allocationType && onSubmit({
      nodeId: getCurrentUserQuery.data.siblingNodeId,
      allocationTypeCode: userDepotAllocation.allocationType
    });
  };

  return (
    <Grid container spacing={2} alignItems="center" width="100%">
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Stack direction="row" width="100%">
          <IconContainer backgroundColor="transparent"><SecondaryAllocationIcon /></IconContainer>
          <LiveSearchWrapper>
            <LiveSearchBox
              title="Allocation"
              multiline={multiline}
              timeOffset={400}
              value={userDepotAllocation?.node?.name || ''}
              defaultSuggetions={defaultAllocationSuggetions}
              onClearValue={() => setUserDepotAllocation(allocation)}
              onChange={async (obj: LocationNodeData) => {
                if (obj.id && obj.name) {
                  setUserDepotAllocation({
                    ...userDepotAllocation,
                    node: {
                      id: obj.id,
                      name: obj.name,
                      code: obj.code,
                      nodeTypeCode: obj.nodeTypeCode,
                      liveSearch: true
                    }
                  });
                }
              }}
              onApiInvoke={async (name) => {
                return await searchAllocationNodes.mutateAsync(name);
              }}
              renderItem={(props: any, option: any) => {
                return (
                  <StyledBox heighlightBackground={option.isSuggestedNode}>
                    <LiveSearchListItem {...props}>
                      <LocationSearchItem data={option} />
                    </LiveSearchListItem>
                  </StyledBox>
                );
              }}
            />
          </LiveSearchWrapper>
          <AllocationSubmitButtonWrap>
            <Button variant={ButtonVariantProps.Primary} color={ButtonColorProps.Success} onClick={handleSubmit}>
              Save
            </Button>
          </AllocationSubmitButtonWrap>
        </Stack>
      </Grid>
      <Grid
        alignItems="center"
        justifyContent={{ xs: 'flex-end', md: 'flex-start' }}
        display="flex"
        flexWrap="wrap"
        gap="10px"
        size={{
          xs: 12,
          md: 8
        }}>
        {userDepotAllocation?.parentNode?.id && userDepotAllocation?.parentNode?.name && userDepotAllocation?.parentNode?.id === allocation?.parentNode?.id ?
          <Button
            color={ButtonColorProps.Warning}
            variant={ButtonVariantProps.Secondary}
            startIcon={<ArrowUpwardIcon />}
            onClick={handleParentNode}
          >
            {userDepotAllocation?.parentNode?.name}
          </Button>
          : 
          null
        }
        <Stack direction="row" gap="10px">
          {isUserHasPermission(ActionPermissions.Service_Edit_Allocation_Send_Up_To, permissions) && 
            <Button
              color={ButtonColorProps.Warning}
              variant={ButtonVariantProps.Secondary}
              onClick={handleTake}
            >
                Take
            </Button>
          }
          <Button variant={ButtonVariantProps.Secondary} color={ButtonColorProps.Error} onClick={onClose}>
            Cancel
          </Button>
        </Stack>
        <Stack direction="row" gap="30px" ml={5}>
          {permissions && (userDepotAllocation?.node?.liveSearch || userDepotAllocation?.node?.isVisible) && getAllocationNodeAccess(permissions, userDepotAllocation, false).hasAccess &&
            <Link href={getAllocationNodeAccess(permissions, userDepotAllocation, false).url}>{userDepotAllocation?.node?.name}</Link> 
          }
          {permissions && (userDepotAllocation?.parentNode?.liveSearch || userDepotAllocation?.parentNode?.isVisible) && getAllocationNodeAccess(permissions, userDepotAllocation, true).hasAccess &&
            <Link href={getAllocationNodeAccess(permissions, userDepotAllocation, true).url}>{userDepotAllocation?.parentNode?.name}</Link> 
          }
        </Stack>
      </Grid>
    </Grid>
  );
};